import { FC, useEffect, useState } from "react";
import AuthService from "../../services/auth.service";
import {
  EXTERNAL_TOKEN_PARAM,
  EXTERNAL_ACCESS_TOKEN_PARAM,
  EXTERNAL_ID_TOKEN_PARAM,
} from "../../core/constants";
import jwtDecode from "jwt-decode";
import { KJUR } from "jsrsasign";

interface EmbeddedAuthProps {
  children: React.ReactElement;
}

/**
 * Stores tokens in the same format as OneLogin ImplicitFlow
 * This makes the embedded mode transparent to the rest of the app
 */
const storeTokensLikeOneLogin = (
  accessToken: string,
  idToken: string,
): void => {
  const isValidIdToken = validateIdToken(idToken);
  if (isValidIdToken.isValid) {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("idToken", idToken);
  } else {
    console.error("Token validation failed:", isValidIdToken.errors);
  }
};

/**
 * Validate the basic format of the JWT token
 */
const isValidFormat = (token: string): boolean => {
  if (!token || typeof token !== "string") {
    return false;
  }

  // Verify that the token has the correct format (3 parts separated by dots)
  const parts = token.split(".");
  if (parts.length !== 3) {
    return false;
  }

  return true;
};

/**
 * Validate that the token has the correct origin
 */
const hasCorrectOrigin = (origin: string): boolean => {
  try {
    const allowedOrigins = ["goodneighbortech"];
    return allowedOrigins.includes(origin);
  } catch {
    return false;
  }
};

/**
 * Validate public key from parent window
 * This methods allow to add possible future origins with different public keys
 */
const validateAccessParentWindow = (token: string, origin: string): boolean => {
  try {
    let publicKeyBase64: string | undefined;
    switch (origin) {
      case "goodneighbortech": {
        publicKeyBase64 = process.env.REACT_APP_PUBLIC_KEY_FROM_GNT;
        break;
      }
      default: {
        return false;
      }
    }
    const publicKey = atob(publicKeyBase64 ?? "");
    const acceptField = { alg: ["RS256"] };
    return KJUR.jws.JWS.verifyJWT(token, publicKey, acceptField);
  } catch (error) {
    console.error("Error validating public key from parent window", error);
    return false;
  }
};

/**
 * Check if accessToken is expired or expiring soon
 * Returns: { isExpired: boolean, expiresIn: number (seconds), isExpiringSoon: boolean }
 */
const checkTokenExpiration = (
  accessToken: string | null,
): {
  isExpired: boolean;
  expiresIn: number;
  isExpiringSoon: boolean;
} => {
  if (!accessToken) {
    return { isExpired: true, expiresIn: 0, isExpiringSoon: true };
  }

  try {
    const decoded = jwtDecode<any>(accessToken);
    const exp = decoded.exp; // Expiration time in seconds (Unix timestamp)
    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    const expiresIn = exp - now; // Time until expiration in seconds
    const isExpired = expiresIn <= 0;
    // Consider token expiring soon if it expires in less than 10 minutes (600 seconds)
    const isExpiringSoon = expiresIn > 0 && expiresIn < 600;

    return { isExpired, expiresIn, isExpiringSoon };
  } catch (error) {
    console.error("Error checking token expiration", error);
    return { isExpired: true, expiresIn: 0, isExpiringSoon: true };
  }
};

/**
 * General idToken validations
 */
const validateIdToken = (
  token: string,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate format
  if (!isValidFormat(token)) {
    errors.push("Invalid JWT format");
  }

  const decoded = jwtDecode<any>(token);
  const origin = decoded.origin;

  // Validate origin
  if (!hasCorrectOrigin(origin)) {
    errors.push("Invalid origin");
  }

  // Validate public key from parent window
  if (!validateAccessParentWindow(token, origin)) {
    errors.push("Invalid public key from parent window");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const EmbeddedAuth: FC<EmbeddedAuthProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Try to get tokens from URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get(EXTERNAL_TOKEN_PARAM);
    const accessToken = urlParams.get(EXTERNAL_ACCESS_TOKEN_PARAM);
    const idToken = urlParams.get(EXTERNAL_ID_TOKEN_PARAM);

    // Check if we have tokens in URL and if they are different from existing ones
    const existingAccessToken = localStorage.getItem("accessToken");
    const existingIdToken = localStorage.getItem("idToken");

    // Determine which tokens we'll use (from URL or existing)
    const tokenToUse = token || accessToken;

    // Check if tokens are actually new (different from existing ones)
    const hasNewTokens = !!(
      tokenToUse &&
      (tokenToUse !== existingAccessToken ||
        (idToken && idToken !== existingIdToken))
    );

    // Process tokens from URL params if we have new ones
    if (hasNewTokens) {
      // Process tokens from URL params
      // Prefer accessToken and idToken if both are provided
      if (accessToken && idToken) {
        storeTokensLikeOneLogin(accessToken, idToken);
        // Only set initialized if tokens were actually stored
        const storedToken = localStorage.getItem("accessToken");
        if (storedToken) {
          setIsInitialized(true);
        } else {
          console.error(
            "EmbeddedAuth: Token validation failed, tokens not stored",
          );
        }
      } else if (tokenToUse && idToken) {
        // Use token/accessToken with idToken
        storeTokensLikeOneLogin(tokenToUse, idToken);
        // Only set initialized if tokens were actually stored
        const storedToken = localStorage.getItem("accessToken");
        if (storedToken) {
          setIsInitialized(true);
        } else {
          console.error(
            "EmbeddedAuth: Token validation failed, tokens not stored",
          );
        }
      } else if (tokenToUse) {
        // Fallback: single token, use for both
        storeTokensLikeOneLogin(tokenToUse, tokenToUse);
        // Only set initialized if tokens were actually stored
        const storedToken = localStorage.getItem("accessToken");
        if (storedToken) {
          setIsInitialized(true);
        } else {
          console.error(
            "EmbeddedAuth: Token validation failed, tokens not stored",
          );
        }
      }
    } else if (tokenToUse && tokenToUse === existingAccessToken) {
      // Tokens match existing ones, no need to replace
      // Use existing tokens - already initialized
      setIsInitialized(true);
    } else if (window.parent !== window) {
      // In iframe but no tokens in URL, wait for postMessage from parent
      // Don't set initialized yet, wait for AUTH_TOKEN message
      // This handles the case where GNT embeds the app in an iframe
    } else if (existingAccessToken && existingIdToken) {
      // Not in iframe but have tokens - might be from previous session
      // Only use them if we're explicitly in embedded mode (shouldn't happen, but safe fallback)
      setIsInitialized(true);
    } else {
      // No auth provided and not in iframe, show error
      console.warn("Embedded mode: No authentication provided");
      setIsInitialized(true); // Still render to show error state
    }

    // Handle postMessage from parent window (for iframe embedding)
    const handleMessage = (event: MessageEvent) => {
      const allowedOrigins = [
        "http://localhost:3000",
        "https://localhost:3000",
        "https://app.qa.goodneighbortech.com",
        "https://app.goodneighbortech.com",
      ];
      if (!allowedOrigins.includes(event.origin)) return;

      if (event.data.type === "AUTH_TOKEN") {
        const {
          token: msgToken,
          accessToken: msgAccessToken,
          idToken: msgIdToken,
        } = event.data;

        // Expect tokens in OneLogin format: accessToken and idToken
        if (msgAccessToken && msgIdToken) {
          storeTokensLikeOneLogin(msgAccessToken, msgIdToken);
          setIsInitialized(true);
        } else if (msgToken) {
          // If only single token provided, use it for both (fallback)
          storeTokensLikeOneLogin(msgToken, msgToken);
          setIsInitialized(true);
        }
      } else if (event.data.type === "TOKEN_REFRESH") {
        // Handle token refresh from parent system
        const {
          token: refreshedToken,
          accessToken: refreshedAccessToken,
          idToken: refreshedIdToken,
        } = event.data;

        if (refreshedAccessToken && refreshedIdToken) {
          storeTokensLikeOneLogin(refreshedAccessToken, refreshedIdToken);
          // Notify AuthService that token was refreshed (for interceptor)
          // Pass accessToken as the main token (for Authorization header)
          // and idToken separately (for IdentityToken header in SmartDeviceService)
          AuthService.onTokenRefreshed(
            refreshedAccessToken, // This is the token used in Authorization header
            refreshedAccessToken, // accessToken parameter
            refreshedIdToken, // idToken parameter (for IdentityToken header)
          );
        } else if (refreshedToken) {
          // Fallback: use single token for both
          storeTokensLikeOneLogin(refreshedToken, refreshedToken);
          // Notify AuthService that token was refreshed
          AuthService.onTokenRefreshed(
            refreshedToken,
            refreshedToken,
            refreshedToken,
          );
        }
      }
    };

    // Listen for messages from parent window
    window.addEventListener("message", handleMessage);

    // Request auth from parent if in iframe
    if (window.parent !== window) {
      window.parent.postMessage({ type: "REQUEST_AUTH" }, "*");
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Check token expiration every 5 minutes
  useEffect(() => {
    // Only start expiration checks if initialized and token is available
    if (!isInitialized) {
      return;
    }

    const accessToken = AuthService.accessToken;
    if (!accessToken) {
      // Token not available yet, wait a bit and check again
      const checkTokenAvailable = setTimeout(() => {
        const token = AuthService.accessToken;
        if (!token) {
          console.warn("No access token available for expiration check");
        }
      }, 100);
      return () => clearTimeout(checkTokenAvailable);
    }

    // Check immediately
    const checkExpiration = () => {
      const currentToken = AuthService.accessToken;
      if (!currentToken) {
        console.warn("Access token not available during expiration check");
        return;
      }
      const expirationStatus = checkTokenExpiration(currentToken);

      if (expirationStatus.isExpired) {
        // Notify parent window that token is expired
        if (window.parent !== window) {
          window.parent.postMessage({ type: "TOKEN_EXPIRED" }, "*");
        }
      } else if (expirationStatus.isExpiringSoon) {
        // Notify parent window that token is expiring soon
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: "TOKEN_EXPIRING_SOON",
              expiresIn: expirationStatus.expiresIn,
            },
            "*",
          );
        }
      }
    };

    // Check immediately
    checkExpiration();

    // Set up interval to check every 5 minutes (300000 ms)
    const intervalId = setInterval(
      () => {
        checkExpiration();
      },
      5 * 60 * 1000,
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [isInitialized]);

  if (!isInitialized) {
    return <div>Initializing authentication...</div>;
  }

  return children;
};

export default EmbeddedAuth;
